/**
 * Translation layer for the service catalogue.
 *
 * The English copy lives in data/services.js, which keeps the catalogue
 * readable and lets a new service be added in one place. Translations live in
 * the locale files under flat keys, so they travel with every other string on
 * the site and fall back to English automatically when one is missing:
 *
 *   "svc.rag.title":      "RAG სისტემები: ..."
 *   "svc.rag.intro.0":    "..."
 *   "svc.rag.off.2.t":    "..."   // offering 3, title
 *   "svc.rag.faq.1.a":    "..."   // FAQ 2, answer
 *
 * Fields that are not prose — slug, category, visual, stack, caseStudy,
 * related, seoTitle and seoDescription — stay as they are. Page titles and
 * meta descriptions are deliberately English: only the English pages are
 * prerendered for search engines.
 */

const pick = (translations, key, fallback) => {
  const value = translations[key]
  return typeof value === 'string' && value.trim() ? value : fallback
}

export const serviceKey = (slug, field) => `svc.${slug}.${field}`

/**
 * Returns the service with its prose swapped for the active language.
 * `translations` is the map from LanguageContext.
 */
export const localizeService = (service, translations) => {
  if (!service || !translations) return service
  const t = (field, fallback) => pick(translations, serviceKey(service.slug, field), fallback)

  return {
    ...service,
    name: t('name', service.name),
    title: t('title', service.title),
    summary: t('summary', service.summary),
    tagline: t('tagline', service.tagline),
    lead: t('lead', service.lead),
    statement: t('statement', service.statement),
    intro: service.intro.map((paragraph, i) => t(`intro.${i}`, paragraph)),
    offerings: service.offerings.map((offering, i) => ({
      title: t(`off.${i}.t`, offering.title),
      text: t(`off.${i}.d`, offering.text),
    })),
    useCases: service.useCases.map((useCase, i) => t(`use.${i}`, useCase)),
    process: service.process.map((step, i) => ({
      title: t(`proc.${i}.t`, step.title),
      text: t(`proc.${i}.d`, step.text),
    })),
    faq: service.faq.map((item, i) => ({
      question: t(`faq.${i}.q`, item.question),
      answer: t(`faq.${i}.a`, item.answer),
    })),
    // "svc.rag.deep.title", "svc.rag.deep.2.t", "svc.rag.deep.2.p.0"
    deepDive: service.deepDive && {
      title: t('deep.title', service.deepDive.title),
      items: service.deepDive.items.map((item, i) => ({
        title: t(`deep.${i}.t`, item.title),
        text: item.text.map((paragraph, j) => t(`deep.${i}.p.${j}`, paragraph)),
      })),
    },
  }
}

export const localizeServices = (services, translations) =>
  services.map((service) => localizeService(service, translations))

export default localizeService
